import React from "react"
import katex from "katex"
import "katex/dist/katex.min.css"
import "./index.css"

export interface IMathKatex {
    mixedContent: string
    className?: string
    classNameForMath?: string
}

/**
 * This will parse the combined html (or regular text) and math items into an array
 * 
 * @param mixedContent 
 * @returns 
 */
function parseHtmlWithMath(mixedContent) {
    let regex = /`([^`]+)`|([^`]+)/g
    let result: any[] = []
    let match

    mixedContent = mixedContent.replace(/\n/g, "<br/>")

    while ((match = regex.exec(mixedContent)) !== null) {
        let matchedContent

        if (match[1]) {
            matchedContent = (match[1] || "").trim()
            result.push({ type: 'math', content: matchedContent })
        } 
        else if (match[2]) {
            matchedContent = (match[2] || "").trim()
            result.push({ type: 'html', content: matchedContent })
        }
    }

    return result
}

// Parse HTML into semantic HTML tags
export function MathKatexBlock({
    mixedContent,
    className = "",
    classNameForMath = "math-katex-default",
    }: IMathKatex) {
    const tokens = parseHtmlWithMath(mixedContent)

    // Rebuild a single HTML string with math already rendered
    const joined = tokens
        .map((t) => t.type === "html" ? t.content : `<span class="${classNameForMath}">${renderMathToString(t.content)}</span>`)
        .join("")

    // Split into logical lines
    const lines = joined
        .split(/<br\s*\/?>(?![^<]*>)/i) // split on <br/> not inside a tag (basic heuristic)
        .map((l) => l.trim())
        .filter((l) => l.length > 0)

    type Block =
        | { type: "p"; text: string }
        | { type: "ol"; items: string[] }
        | { type: "ul"; items: string[] }


    const blocks: Block[] = []
    let currentList: { kind: "ol" | "ul"; items: string[] } | null = null

    const pushParagraph = (text: string) => {
        if (text.length === 0) return
        blocks.push({ type: "p", text })
    }

    const flushList = () => {
        if (currentList) {
            blocks.push({ type: currentList.kind, items: currentList.items })
            currentList = null
        }
    }

    const olRe = /^(\d+)[\.)]\s+(.*)$/
    const ulRe = /^([\u2022*\-])\s+(.*)$/ // bullets: • * -

    for (const raw of lines) {
        const olMatch = raw.match(olRe)
        const ulMatch = raw.match(ulRe)

        if (olMatch) {
            const item = olMatch[2].trim()
            if (!currentList || currentList.kind !== "ol") {
                flushList()
                currentList = { kind: "ol", items: [] }
            }
            currentList.items.push(item)
            continue
        }

        if (ulMatch) {
            const item = ulMatch[2].trim()
            if (!currentList || currentList.kind !== "ul") {
                flushList()
                currentList = { kind: "ul", items: [] }
            }
            currentList.items.push(item)
            continue
        }

        // Regular paragraph line
        flushList()
        pushParagraph(raw)
    }

    flushList()

    const html = blocks
        .map((b) => {
            if (b.type === "p") return `<p>${b.text}</p>`
            if (b.type === "ol") return `<ol>${b.items.map((i) => `<li>${i}</li>`).join("")}</ol>`
            return `<ul>${b.items.map((i) => `<li>${i}</li>`).join("")}</ul>`
        })
        .join("")


    return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
}

/**
 * KaTeX rendering for the expression
 * @param mathExpression 
 * @returns 
 */
function renderMathToString(mathExpression) {
    return katex.renderToString(mathExpression, {throwOnError: false})
}

export default function MathKatex({mixedContent, className="", classNameForMath=""}: IMathKatex) {
    let parsedItems = parseHtmlWithMath(mixedContent)
    let html = parsedItems.reduce((acc, cur) => {
        let {type, content} = cur
        let innerHtml = type === "html" ? 
            `<span>${content}</span>`: 
            `<span class="${classNameForMath}">${renderMathToString(content)}</span>`
        return acc += innerHtml
    }, "")

    return (
        <div className={className} dangerouslySetInnerHTML={{__html: html}}/>
    )
}

export function MathKatex2({mixedContent, className="", classNameForMath="math-katex-default"}: IMathKatex) {
    let parsedItems = parseHtmlWithMath(mixedContent)
    let html = parsedItems.reduce((acc, cur) => {
        let {type, content} = cur
        let innerHtml = type === "html" ? 
            `<span>${content}</span>`: 
            `<span class="${classNameForMath}">${renderMathToString(content)}</span>`
        return acc += innerHtml
    }, "")

    return (
        <span className={className} dangerouslySetInnerHTML={{__html: html}}/>
    )
}
